import { useMemo } from "react";

import { Roles, SurveyActions, SurveyStatus } from "@/shared/utils/constants";
import { FullUserResponse } from "@/redux/users/types";
import { SurveyResponse } from "@/redux/survey/types";
import { SurveyPermissions } from "@/features/survey/hooks/types";

const canUserPerformActionOnSurvey = (
  surveyPermissions: { role: string; permission: string }[],
  role: string,
  action?: null | string,
) => {
  if (surveyPermissions.length === 0) {
    return true;
  }
  const permission = surveyPermissions.find(
    (i) => i.role === role && (!action || i.permission === action),
  );
  return !!permission;
};

export const useSurveyPermissions = (
  survey: SurveyResponse | null | undefined,
  currentUser: FullUserResponse | null | undefined,
): SurveyPermissions => {
  return useMemo(() => {
    if (!currentUser || !survey) {
      return {
        canView: false,
        canEdit: false,
        canSubmit: false,
        canInvite: false,
        isLoading: true,
      };
    }

    const isAppAdmin = currentUser.role === Roles.ADMIN.value;

    const hasViewPermission =
      canUserPerformActionOnSurvey(
        survey.permissions,
        currentUser.organizationRole,
      ) || isAppAdmin;

    const hasSubmitPermission = canUserPerformActionOnSurvey(
      survey.permissions,
      currentUser.organizationRole,
      SurveyActions.SUBMIT.value,
    );

    const hasEditPermissions =
      !survey.details?.isSubmitted &&
      survey.status === SurveyStatus.ACTIVE.value;

    return {
      canView: hasViewPermission,
      canEdit: hasEditPermissions,
      canSubmit: hasSubmitPermission,
      canInvite: isAppAdmin,
      isLoading: false,
    };
  }, [currentUser, survey]);
};
